import React, { FC } from 'react';
import { toast } from 'react-toastify';
import TableTab from '../../../components/tabs/table-tab';
import allReportStatus from '../../../constants/all-report-status';
import { ReportTypes } from '../../../constants/report-types';
import { ReportType } from '../../../constants/system-constants';
import { useLocale } from '../../../contexts/localeContext';
import { TableContextProvider } from '../../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import CustomSettingIcon from '../../../images/Custom Setting.svg';
import InheritPresetIcon from '../../../images/Inherited from Preset.svg';
import { Category } from '../../../models/Category';
import columns from '../../../models/ColumnDefinitions/ReportColumn';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import { Report } from '../../../models/Report';
import searchColumns from '../../../models/search/ReportSearch';
import ReportTypeSearch from '../../../models/search/ReportTypeSearch';
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { TableLegendItem } from '../../../models/table/TableLegendItem';
import { deleteReport, getAllReport, getAllReportTypeNamesWithPagination, getAllTransactionReports, getReportById } from '../../../services/reportService';
import { showError } from '../../../services/utilities/ServiceError';
import { appendFilter } from '../../../utilities/filters';
import { getItemPath } from '../../../utilities/reportUtil';

interface Props {
    isModalOpen: boolean;
    openModal: (isOpen: boolean) => void;
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    setCurrentItem: (item: Report) => void;
    reportType?: ReportType.TRANSACTIONAL | ReportType.TIME_AND_ATTENDANCE;
    userProfileId?: number;
}

const AllReport: FC<Props> = ({ setCurrentItem, setBusy, categories, setCategories, openModal, reportType, userProfileId }) => {
    const { translate } = useLocale();
    const lookup: Lookup[] = [
        {
            name: LookupTypes.reportType,
            values: [],
            dataMethod: getAllReportTypeNamesWithPagination,
            searchAccessor: ReportTypeSearch.find(item => item.accessor === "type")?.searchOnName
        }
    ];

    const tableLegendItems: TableLegendItem[] = [
        {
            value: allReportStatus.Custom.id,
            imageIcon: CustomSettingIcon,
            label: translate.byKey(allReportStatus.Custom.translationKey) ?? allReportStatus.Custom.label,
            tooltip: translate.byKey(allReportStatus.Custom.translationKey) ?? allReportStatus.Custom.label
        },
        {
            value: allReportStatus.Template.id,
            imageIcon: InheritPresetIcon,
            label: translate.byKey(allReportStatus.Template.translationKey) ?? allReportStatus.Template.label,
            tooltip: translate.byKey(allReportStatus.Template.translationKey) ?? allReportStatus.Template.label
        }
    ];

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteReport(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const editReport = async (id: string) => {
        try {
            const timeTriggeredAction = await getReportById(id);
            setCurrentItem(timeTriggeredAction)
            openModal(true);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const newReportSearchColumns = searchColumns.filter(column => {
        return column.accessor !== 'type'
    });

    const newReportColumns = columns.map(column => {
        return { ...column, active: column.accessor === 'type' ? false : column.active }
    });

    const getTitle = () => {
        switch (reportType) {
            case ReportType.TRANSACTIONAL:
                return translate.byKey(TranslationKeys.transactions);
            case ReportType.TIME_AND_ATTENDANCE:
                return translate.byKey(TranslationKeys.timeAndAttendance);
            default:
                return translate.byKey(TranslationKeys.allReports);
        }
    }

    const getDataMethod = (listParameters) => {
        switch (reportType) {
            case ReportType.TRANSACTIONAL:
                return getAllTransactionReports(listParameters);
            case ReportType.TIME_AND_ATTENDANCE:
                return getAllReport({ ...listParameters, filters: appendFilter([ReportTypes.ABSENTEEISM, ReportTypes.FIRST_IN_LAST_OUT, ReportTypes.OCCUPANCY], 'Report type id', 'reportType.id',[]) }, userProfileId);
            default:
                return getAllReport(listParameters, userProfileId);
        }
    }

    const getTableDescriptor = () => {
        switch (reportType) {
            case ReportType.TRANSACTIONAL:
            case ReportType.TIME_AND_ATTENDANCE:
                return newReportColumns;
            default:
                return columns;
        }
    }

    const getSearchDescriptor = () => {
        switch (reportType) {
            case ReportType.TRANSACTIONAL:
            case ReportType.TIME_AND_ATTENDANCE:
                return newReportSearchColumns;
            default:
                return searchColumns;
        }
    }

    return <TableContextProvider
        dataMethod={(listParameters) => getDataMethod(listParameters)}
        tableDescriptor={getTableDescriptor()}
        searchDescriptor={getSearchDescriptor()}
        legendItems={tableLegendItems}
        useLegend
        useTable
        usePagination
        useSort
        useSearch
        deleteMethod={onDelete}
        editMethod={editReport}
        useFilter
        lookup={lookup}
        customDelete={{ fieldName: "status", comparisonValue: allReportStatus.Custom.id }}
        customEdit={{ fieldName: "status", comparisonValue: allReportStatus.Custom.id }} >
        <TableTab
            setShowModal={openModal}
            title={getTitle()}
            selectRows={false}
            rowPath={getItemPath(reportType ?? '', true)} 
            cyName="allReport"/>
    </TableContextProvider>;
}

export default AllReport;
